/**
 * 图生图 prompt 里用「Image 2」指第几张参考图（编号 = 上传区里的位置，也是 image_input 的顺序）。
 * 发给模型的就是这几个字：Seedream 官方多图示例就是按 Image N 指图，其他模型也能读懂，
 * 所以接口、数据库、历史记录都不用改。这里只放纯函数，方便单测（test/image-refs.test.mjs）。
 */

/** 「Image 2」「image2」「IMAGE 2」都算；不认「image 2.5」这类小数 */
const IMAGE_REF_PATTERN = /\bimage ?(\d{1,2})\b(?!\.\d)/gi;

export interface ImageRefMatch {
	start: number;
	end: number;
	n: number;
}

export interface PromptPart {
	text: string;
	/** 有值说明这一段是图片编号 */
	ref?: number;
	/** 编号在 1..imageCount 之间 */
	valid?: boolean;
}

export function formatImageRef(n: number): string {
	return `Image ${n}`;
}

export function findImageRefs(text: string): ImageRefMatch[] {
	return Array.from(text.matchAll(IMAGE_REF_PATTERN), (match) => ({
		start: match.index,
		end: match.index + match[0].length,
		n: Number(match[1]),
	}));
}

function isValidRef(n: number, imageCount: number) {
	return n >= 1 && n <= imageCount;
}

/** 输入框高亮用：把文字切成普通段和编号段 */
export function splitImageRefs(text: string, imageCount: number): PromptPart[] {
	const parts: PromptPart[] = [];
	let cursor = 0;
	for (const match of findImageRefs(text)) {
		if (match.start > cursor) {
			parts.push({ text: text.slice(cursor, match.start) });
		}
		parts.push({
			text: text.slice(match.start, match.end),
			ref: match.n,
			valid: isValidRef(match.n, imageCount),
		});
		cursor = match.end;
	}
	if (cursor < text.length) {
		parts.push({ text: text.slice(cursor) });
	}
	return parts;
}

/** 光标停在哪个编号上（含两端），用来高亮上传区对应那张；不在编号上返回 null */
export function imageRefAt(
	text: string,
	caret: number,
	imageCount: number,
): number | null {
	const match = findImageRefs(text).find(
		(ref) => caret >= ref.start && caret <= ref.end,
	);
	return match && isValidRef(match.n, imageCount) ? match.n : null;
}

/** 埋点用：prompt 里指向现有图片的编号个数 */
export function countImageRefs(text: string, imageCount: number): number {
	return findImageRefs(text).filter((ref) => isValidRef(ref.n, imageCount))
		.length;
}

/** 第一个指向不存在的图的编号（image 4 但只有 2 张），用来出黄色提示 */
export function firstMissingImageRef(
	text: string,
	imageCount: number,
): number | null {
	return (
		findImageRefs(text).find((ref) => !isValidRef(ref.n, imageCount))?.n ??
		null
	);
}

/**
 * 按 keep 的结果逐个改写编号：返回 null 表示删掉这个编号，返回数字表示改成这个编号。
 * 删掉时顺手去掉多出来的空格（「from Image 1 in」→「from in」，「Image 1, keep」→「, keep」前不留空格）。
 */
function rewriteImageRefs(
	text: string,
	rewrite: (n: number) => number | null,
): string {
	let result = text;
	// 从后往前改，前面的下标不受影响
	for (const ref of findImageRefs(text).reverse()) {
		const next = rewrite(ref.n);
		if (next === ref.n) {
			continue;
		}
		if (next !== null) {
			// 只换数字，保留用户原来的写法（image2 / Image 2）
			const original = result.slice(ref.start, ref.end);
			const replaced = original.replace(/\d+$/, String(next));
			result =
				result.slice(0, ref.start) + replaced + result.slice(ref.end);
			continue;
		}
		let start = ref.start;
		let end = ref.end;
		const before = result[start - 1];
		const after = result[end];
		if (
			before === " " &&
			(after === undefined || /[\s,.;:!?)]/.test(after))
		) {
			start -= 1;
		} else if ((before === undefined || before === "\n") && after === " ") {
			end += 1;
		}
		result = result.slice(0, start) + result.slice(end);
	}
	return result;
}

/** 删掉第 n 张图：指向它的编号删掉，后面的编号往前补一位 */
export function removeImageRef(text: string, n: number): string {
	return rewriteImageRefs(text, (ref) =>
		ref === n ? null : ref > n ? ref - 1 : ref,
	);
}

/** 换模型张数上限变小时：超出 max 的编号删掉 */
export function dropImageRefsAbove(text: string, max: number): string {
	return rewriteImageRefs(text, (ref) => (ref > max ? null : ref));
}

/** Clear all：所有编号都删掉，其余文字保留 */
export function dropAllImageRefs(text: string): string {
	return rewriteImageRefs(text, () => null);
}

/**
 * 把 [start, end) 换成一个或几个编号（「Image 3, Image 4」），前后按需补空格。
 * 返回新文字和插入后光标该放的位置。
 */
export function insertImageRefs(
	text: string,
	start: number,
	end: number,
	ns: number[],
): { text: string; caret: number } {
	const before = text.slice(0, start);
	const after = text.slice(end);
	const lead = before.length > 0 && !/\s$/.test(before) ? " " : "";
	const trail = after.length > 0 && /^[\s,.;:!?)]/.test(after) ? "" : " ";
	const inserted = lead + ns.map(formatImageRef).join(", ") + trail;
	return {
		text: before + inserted + after,
		caret: before.length + inserted.length,
	};
}
